import React, { FunctionComponent, ReactNode, ChangeEvent, useEffect } from 'react'
import { Checkbox, Heading, Text, Link } from '@afterpaytouch/core'
import { useTranslation, Trans } from 'next-i18next'

import { ModuleContainer } from '..'
import { useCheckoutSizes } from '../../utils/responsive'
import { useCheckoutColor } from '../../utils/color'
import {
  setDonationAmount,
  setDonationStatus,
  setHasUpdatedDonation,
  setIsUpdatingDonation,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useDonation,
  useDonationCampaignDetails,
  useIsBillingAgreementApprovalProcess,
  useIsDonationEnabled,
} from '../../state'
import { CheckoutStatus, DonationStatus } from '../../model'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useMoney } from '../Money/useMoney'

export const Donation: FunctionComponent = () => {
  const { locale } = useAppRouter()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { subtextColor } = useCheckoutColor()
  const { status } = useCheckoutState()
  const donationCampaignDetails = useDonationCampaignDetails()
  const isBillingAgreementApprovalFlow = useIsBillingAgreementApprovalProcess()

  const isDisabled = status > CheckoutStatus.Started
  const { donationAmount, hasUpdatedDonation } = useDonation()
  const isDonationEnabled = useIsDonationEnabled()

  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { formatMoney } = useMoney({ locale, currency: donationCampaignDetails?.amount.currency })

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setIsUpdatingDonation(true))
    setTimeout(() => {
      const isChecked = e.target.checked
      dispatch(setDonationAmount(isChecked ? Number(donationCampaignDetails?.amount.amount) : 0))
      dispatch(setIsUpdatingDonation(false))
      logEvent(TrackingEvent.TOGGLED_DONATION_BUTTON, { checked: isChecked })
    }, 700)
  }

  const renderContent = (): ReactNode => {
    const campaignName = donationCampaignDetails?.campaignId
    const amount = formatMoney(donationCampaignDetails?.amount.amount)
    return (
      <>
        <Heading size={textSize}>{t(`donation:${campaignName}:title`, { amount })}</Heading>
        <Text size={smallTextSize} color={subtextColor}>
          <Trans
            i18nKey={`donation:${campaignName}:body`}
            tOptions={{ amount }}
            components={{
              l: <Link kind='Underline' href={t('donation:termsUrl')} target='_blank' />,
            }}
          />
        </Text>
      </>
    )
  }

  useEffect(() => {
    if (!hasUpdatedDonation) {
      dispatch(setHasUpdatedDonation(true))
      if (isDonationEnabled) {
        dispatch(setDonationStatus(DonationStatus.Active))
      } else {
        dispatch(setDonationStatus(DonationStatus.Inactive))
      }
    }
  }, [isDonationEnabled])

  if (!isDonationEnabled || isBillingAgreementApprovalFlow) {
    return null
  }

  return (
    <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
      <Checkbox
        size='Small'
        defaultChecked={!!donationAmount}
        disabled={isDisabled}
        label={renderContent()}
        onChange={handleOnChange}
        testNameSpace='donation'
      />
      <TrackOnMount eventName={TrackingEvent.VIEWED_DONATION} eventProperties={{ campaign: donationCampaignDetails?.campaignId }} />
    </ModuleContainer>
  )
}
